/**
 * The board preview beside `/settings/delivery-flow` (#2501): how the
 * logistics board will look with this flow. Read-only and computed from the
 * same `GET /api/delivery-flow` the page shows: the columns in flow order and
 * one row per day of the board range, rest days drawn as gap rows. A save
 * refreshes the flow, so the preview follows it.
 *
 * The board itself reads the backend's `rest_day`; this is only a rehearsal of
 * the settings. It reads no board data, so the cells stay empty.
 */
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { useToday } from '@resource-kit/controllers/react';
import { formatBatchDay } from '@features/dispatch';
import { COLUMN_KEY, type DeliveryFlow } from './deliveryFlowShared';
import { previewRows } from './deliveryFlowModel';

/** The board's gap-row hatching (`BatchBoard` `GAP_ROW_STYLE`). */
const GAP_ROW_STYLE: CSSProperties = {
  backgroundImage: 'repeating-linear-gradient(135deg, transparent 0 6px, var(--border) 6px 7px)',
};

export function DeliveryFlowPreview({ flow }: { flow: DeliveryFlow }) {
  const { t, i18n } = useTranslation();
  const today = useToday();
  const rows = previewRows(today, flow.board_range_days, flow.rest_weekdays);
  const colSpan = flow.columns.length + 1;

  return (
    <aside
      aria-label={t('deliveryFlow.preview.label')}
      data-testid="delivery-flow-preview"
      className="space-y-3"
    >
      <p className="text-xs text-muted-foreground">{t('deliveryFlow.preview.label')}</p>
      <div className="overflow-hidden rounded-xl border bg-card shadow-xs">
        <table className="w-full table-fixed border-collapse text-[11.5px]">
          <colgroup>
            <col style={{ width: 78 }} />
            {flow.columns.map((column) => (
              <col key={column} />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th
                scope="col"
                className="truncate px-2 py-2 text-left font-medium text-muted-foreground"
              >
                {t('deliveryFlow.preview.batchColumn')}
              </th>
              {flow.columns.map((column) => (
                <th
                  key={column}
                  scope="col"
                  className="truncate px-1 py-2 text-left font-medium text-muted-foreground"
                >
                  {t(COLUMN_KEY[column].name)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) =>
              row.rest ? (
                <tr
                  key={row.date}
                  data-testid="delivery-flow-preview-rest"
                  className="border-t border-border/50"
                >
                  <td
                    colSpan={colSpan}
                    className="px-2 py-1 text-muted-foreground"
                    style={GAP_ROW_STYLE}
                  >
                    {t('dispatch.board.restDay', { date: formatBatchDay(row.date, i18n.language) })}
                  </td>
                </tr>
              ) : (
                <tr
                  key={row.date}
                  data-testid="delivery-flow-preview-row"
                  className="border-t border-border/50"
                >
                  <th scope="row" className="px-2 py-1.5 text-left font-normal whitespace-nowrap">
                    {formatBatchDay(row.date, i18n.language)}
                  </th>
                  {flow.columns.map((column) => (
                    <td key={column} />
                  ))}
                </tr>
              ),
            )}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">{t('deliveryFlow.preview.note')}</p>
    </aside>
  );
}
